// @ts-strict-ignore
import {inject, Injectable, signal} from "@angular/core";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Participant, Scenario, TeamTrainingSession, TrainingRoom, Event, TranslationKey, ExternalAvailabilityResponse} from "@models";
import {UntilDestroy} from "@ngneat/until-destroy";
import {SimEventService, SimSessionService, SimTrainingRoomService} from "@services";
import {DatePipe} from "@angular/common";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";

export type TeamTrainingSession_Step1Form = FormGroup<{
	title: FormControl<string>;
	date: FormControl<Date>;
	trainingRooms: FormControl<TrainingRoom[]>;
	debriefingRooms: FormControl<TrainingRoom[]>;
	liveStreamRooms: FormControl<TrainingRoom[]>;
}>;

export type TeamTrainingSession_Step2Form = FormGroup<{
	scenario: FormControl<Scenario>;
	start: FormControl<string>;
	end: FormControl<string>;
}>;

export type TeamTrainingSession_Step3Form = FormControl<Participant[]>;

@UntilDestroy()
@Injectable()
export class TeamTrainingSessionStateService {
	// Signal
	public isLoading = signal<boolean>(true);

	// Forms
	public step1Form: TeamTrainingSession_Step1Form;
	public step2Form: FormArray<TeamTrainingSession_Step2Form>;

	// Properties
	public session: TeamTrainingSession;
	public event: Event;
	public autosavePromise: Promise<TeamTrainingSession | Participant[] | ExternalAvailabilityResponse>;
	public autosaveWarning: TranslationKey | null = null;
	private _formBuilder = new FormBuilder();

	// Injections
	private _sessionService = inject(SimSessionService);
	private _eventService = inject(SimEventService);
	private _datePipe = inject(DatePipe);
	private _translate = inject(TranslateService);
	private _roomService = inject(SimTrainingRoomService);
	private T = Translations;

	public async initState(sessionId: string | undefined, eventId: string, eventDay: Date | string): Promise<void> {
		this.session = (!!sessionId ? await this._sessionService.getSessionById(sessionId) : {id: "", eventId}) as TeamTrainingSession;
		this.event = (!!eventId ? await this._eventService.getEventById(eventId) : {}) as Event;
		this._initForm(eventDay).then(() => this.isLoading.set(false));
	}

	private async _initForm(eventDay: Date | string): Promise<void> {
		// Step 1
		this.step1Form = this._formBuilder.group({
			title: new FormControl("", [Validators.required]),
			date: new FormControl(new Date(eventDay), [Validators.required]),
			trainingRooms: new FormControl([], [Validators.required]),
			debriefingRooms: new FormControl([]),
			liveStreamRooms: new FormControl([]),
		});
		if (this.session.id) {
			this.step1Form.setValue({
				title: this.session.title,
				date: new Date(this.session.startTime),
				trainingRooms: await this._roomService.getTrainingRoomsByIds(this.session.reservedRoomIds),
				liveStreamRooms: await this._roomService.getTrainingRoomsByIds(this.session.planedLiveStreamRoomIds),
				debriefingRooms: await this._roomService.getTrainingRoomsByIds(this.session.planedDebriefingRoomIds),
			});
		}

		// Step 2
		this.step2Form = new FormArray([], [Validators.required]);
		if (this.session.scenarioRecordingGroups?.length) {
			const runs = this.session.scenarioRecordingGroups.map((scenarioRecordingGroup) =>
				scenarioRecordingGroup.scenarioRecordingsMap.at(0),
			);
			runs.forEach((run, index) => {
				const formGroup = this._formBuilder.group({
					scenario: new FormControl<Scenario>({id: run.scenarioId} as Scenario, [Validators.required]),
					start: new FormControl<string>(
						this._datePipe.transform(
							new Date(this.session.scenarioRecordingGroups.at(index).plannedStart),
							this._translate.instant(this.T.DATE_FORMAT_TIME),
						),
						[Validators.required],
					),
					end: new FormControl<string>(
						this._datePipe.transform(
							new Date(this.session.scenarioRecordingGroups.at(index).plannedEnd),
							this._translate.instant(this.T.DATE_FORMAT_TIME),
						),
						[Validators.required],
					),
				});
				this.step2Form.push(formGroup);
			});
		}
	}
}
